/**
 * 余额与账单：用户信息 + 积分流水（多路径兼容，无接口时返回空列表）
 */

import api from '@/utils/api'
import { mapUserToCreditsProfile } from './creditsRechargeApi'
import { getApiErrorMessage } from './vipSkuApi'
import { uploadVideoMediaFile } from './videoImageApi'

function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(msg)
}

function unwrapList(res) {
  if (res == null) return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.list)) return res.list
  if (Array.isArray(res.records)) return res.records
  const d = res.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.list)) return d.list
    if (Array.isArray(d.data)) return d.data
    if (Array.isArray(d.records)) return d.records
  }
  return []
}

function num(v, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * @param {object} u 用户对象
 */
export function mapUserToBalanceSummary(u = {}) {
  const profile = mapUserToCreditsProfile({ data: { user: u }, user: u })
  const total = num(u.score ?? u.credits ?? u.balance ?? profile.score)
  const generalAvailable = num(
    u.general_score ?? u.common_score ?? u.universal_score ?? u.score ?? total
  )
  const generalFrozen = num(u.general_frozen ?? u.common_frozen ?? u.frozen_score)
  const modelAvailable = num(u.model_score ?? u.model_credits)
  const modelFrozen = num(u.model_frozen ?? u.model_frozen_score)
  const subscription = num(u.subscription_score ?? u.subscribe_score ?? u.vip_score)
  const topup = num(u.recharge_score ?? u.topup_score ?? u.charge_score)
  const other = num(u.other_score ?? u.bonus_score ?? u.reward_score)
  const vipnameRaw = u.vipname ?? u.vip_name
  const vipname =
    vipnameRaw != null && String(vipnameRaw).trim()
      ? String(vipnameRaw).trim()
      : profile.levelLabel || 'FREE'
  const vipEndAt = u.vip_end_at ?? u.vip_end_time ?? u.vip_expire_at ?? null
  return {
    ...profile,
    vipname,
    vipEndAt: vipEndAt != null ? String(vipEndAt) : '',
    total,
    generalAvailable,
    generalFrozen,
    modelAvailable,
    modelFrozen,
    subscription,
    topup,
    other
  }
}

export async function fetchBalanceSummary() {
  const res = await api.get('/user')
  assertApiOk(res)
  const u =
    (res && res.data && res.data.user) ||
    (res && res.user) ||
    (res && res.data) ||
    {}
  return mapUserToBalanceSummary(u)
}

function isScoreGainType(type) {
  return type === 1 || type === '1'
}

/** type === 1 为增加（+），其它为减少（-） */
function resolveScoreChange(row) {
  const raw = num(
    row.change ?? row.score_change ?? row.score ?? row.delta ?? row.amount ?? row.value
  )
  const abs = Math.abs(raw)
  if (abs === 0) return 0
  return isScoreGainType(row.type) ? abs : -abs
}

function mapScoreRecordRow(row) {
  const change = resolveScoreChange(row)
  const title =
    row.title ||
    row.detail ||
    row.desc ||
    row.description ||
    row.remark ||
    row.content ||
    row.type_name ||
    row.type_label ||
    '积分变动'
  const typeLabel =
    row.type_text ||
    row.change_type_name ||
    row.category_name ||
    (isScoreGainType(row.type) ? '获得' : '消耗')
  const date =
    row.created_at ||
    row.create_time ||
    row.time ||
    row.date ||
    row.updated_at ||
    ''
  return {
    id: row.id != null ? String(row.id) : `${title}-${date}-${change}`,
    title: String(title),
    typeLabel: String(typeLabel),
    date: String(date),
    change
  }
}

function resolveScoreRecordsPagination(res, list, page, limit) {
  const root = res && typeof res === 'object' ? res : {}
  const d = root.data != null && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : root
  const total = num(d.total ?? d.count ?? root.total)
  const lastPage = num(d.last_page ?? d.total_pages ?? d.pages)
  const currentPage = num(d.current_page ?? d.page) || page
  let hasMore = false
  if (lastPage > 0) {
    hasMore = currentPage < lastPage
  } else if (total > 0) {
    hasMore = page * limit < total
  } else {
    hasMore = list.length >= limit
  }
  return { page: currentPage, limit, total, hasMore }
}

/**
 * 积分明细 GET /asset-records/score（分页）
 * @returns {{ list: object[], page: number, limit: number, total: number, hasMore: boolean }}
 */
export async function fetchScoreRecords(opts = {}) {
  const page = opts.page ?? 1
  const limit = opts.limit ?? 20
  const params = {
    page,
    limit,
    months: opts.months ?? 3,
    ...(opts.extra || {})
  }
  const ft = opts.filterType
  if (ft === 'gain') params.type = 1
  else if (ft === 'consume') params.type = 0

  const res = await api.get('/asset-records/score', { params })
  assertApiOk(res)
  const rawList = unwrapList(res)
  const list = rawList.map(mapScoreRecordRow).sort((a, b) => {
    const ta = new Date(a.date).getTime() || 0
    const tb = new Date(b.date).getTime() || 0
    return tb - ta
  })
  const meta = resolveScoreRecordsPagination(res, list, page, limit)
  return { list, ...meta }
}

const AVATAR_MAX_BYTES = 5 * 1024 * 1024

/**
 * 上传用户头像图片，返回可访问 URL
 * @param {File|Blob} file
 */
export async function uploadUserAvatar(file) {
  if (!file) throw new Error('请选择图片')
  const type = file.type || ''
  if (type && !/^image\//i.test(type)) {
    throw new Error('仅支持图片格式')
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error('图片不能超过 5MB')
  }
  const name = file.name || 'avatar.jpg'
  const url = await uploadVideoMediaFile({
    file,
    filename: name,
    title: '用户头像',
    relative: true
  })
  return url
}

/**
 * 更新用户资料 PUT /user
 * @param {{ name: string, avatar?: string }} opts
 */
export async function updateUserProfile(opts = {}) {
  const name = String(opts.name || '').trim()
  if (!name) throw new Error('请输入昵称')
  const avatar = String(opts.avatar || '').trim()
  const body = { name }
  if (avatar) body.headimg = avatar
  const res = await api.put('/user', body)
  assertApiOk(res)
  return res
}

/**
 * 卡密兑换 POST /code/use
 * @param {string} code
 */
export async function redeemCodeCard(code) {
  const trimmed = String(code || '').trim()
  if (!trimmed) throw new Error('请输入卡密')
  const res = await api.post('/code/use', { code: trimmed })
  assertApiOk(res)
  return res
}

export { getApiErrorMessage }
